import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Clock, Calendar, Tag, Search, Lightbulb, FileText, Link2, Wrench, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../../sections/Navbar';
import Footer from '../../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const Article1Page = () => {
  const articleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, articleRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={articleRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <article ref={contentRef} className="relative z-10 pt-24 pb-16">
        {/* Hero Image */}
        <div className="animate-section relative h-[50vh] min-h-[400px] overflow-hidden">
          <img 
            src="/blog/article-1-hero.jpg" 
            alt="AI SEO Dashboard" 
            className="w-full h-full object-cover opacity-90"
            style={{ filter: 'hue-rotate(180deg) saturate(0.8)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sky-50 via-transparent to-transparent" />
          
          {/* Content Overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 lg:px-8 pb-12">
            <div className="max-w-4xl mx-auto">
              <Link to="/blog">
                <Button
                  variant="ghost"
                  className="mb-4 text-slate-600 hover:text-slate-800 -ml-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět na blog
                </Button>
              </Link>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-[#0ea5e9]/20 text-[#0ea5e9]">
                  <Tag className="w-3 h-3" />
                  AI SEO
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  6. února 2026
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  8 min čtení
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-800">
                AI mění výsledky vyhledávání: <span className="text-gradient">proč klesají kliky a co s tím</span>
              </h1>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <div className="animate-section">
                <p className="text-slate-600 leading-relaxed text-xl">
                  Dřív stačilo být na první stránce Googlu. Dnes uživatelé často dostanou odpověď přímo ve výsledcích — a vůbec nekliknou. Proč to tak je a jak se přizpůsobit?
                </p>
                
                <p className="text-slate-600 leading-relaxed">
                  Weby na prvních pozicích teď mají méně návštěv. Ne proto, že by SEO přestalo fungovat. Ale protože se změnilo, jak lidé vyhledávají. AI jim dává odpovědi hned — bez nutnosti otevírat stránky.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <Search className="w-7 h-7 text-[#0ea5e9]" />
                  Co se změnilo ve výsledcích
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Klasické vyhledávání ukazovalo seznam odkazů. Dnes často vidíte odpověď rovnou — shrnutí, srovnání, doporučení. To se děje hlavně u dotazů jako:
                </p>
                
                <ul className="space-y-3 my-6">
                  {['jak vybrat...', 'co je lepší...', 'na co si dát pozor...', 'srovnání...'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-[#0ea5e9]" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 leading-relaxed">
                  Lidé chtějí rychlý přehled. Až pak se rozhodují, jestli chtějí vědět víc.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <Lightbulb className="w-7 h-7 text-[#00d4aa]" />
                  Proč lidé méně klikají
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Kliknutí stojí čas a pozornost. Když uživatel dostane odpověď ve vyhledávání, část lidí dál nejde. Pro weby to znamená:
                </p>
                
                <ul className="space-y-4 my-6">
                  {[
                    'Konkurence o pozornost začíná už ve výsledcích',
                    'Návštěvnost jde na stránky, které jsou snadno použitelné jako zdroj',
                    'Důležitý je obsah, který dává jasnou odpověď'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-[#00d4aa] mt-2.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <div className="glass-card rounded-xl p-6 my-8 border-l-4 border-[#0ea5e9]">
                  <p className="text-slate-600 m-0">
                    <strong className="text-slate-800">Tady pomáhá AI SEO:</strong> upravíte obsah tak, aby měl větší šanci být použit jako zdroj — a zároveň vedl k dalšímu kroku.
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">Které weby to pocítí nejvíc</h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Změna je nejvíc vidět tam, kde lidé hledají informace před rozhodnutím:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8">
                  {[
                    { title: 'Produkty a varianty', desc: 'E-shopy' },
                    { title: 'Srovnání', desc: 'Služby, auta, reality' },
                    { title: 'Rozhodovací kritéria', desc: 'B2B, SaaS, odborné služby' },
                    { title: 'Návody a postupy', desc: 'How-to obsah' }
                  ].map((item, i) => (
                    <div key={i} className="glass-card rounded-lg p-5">
                      <div className="font-semibold text-slate-800 mb-1">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <FileText className="w-7 h-7 text-[#14b8a6]" />
                  Jak upravit obsah, aby přinášel výsledky
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Nemusíte přepisovat celý web. Nejčastěji stačí upravit 5–10 klíčových stránek. Tady je jak:
                </p>

                <div className="space-y-6 mt-8">
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3 text-slate-800">
                      <span className="w-8 h-8 rounded-full bg-[#0ea5e9]/20 text-[#0ea5e9] flex items-center justify-center text-sm font-bold">1</span>
                      Pište úvody do 60–90 slov
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      Na každé stránce by měl být hned na začátku jasný úvod:
                    </p>
                    <ul className="space-y-2 mt-4">
                      {['co to je', 'pro koho to je', 'kdy to použít'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3 text-slate-800">
                      <span className="w-8 h-8 rounded-full bg-[#00d4aa]/20 text-[#00d4aa] flex items-center justify-center text-sm font-bold">2</span>
                      Přidejte kritéria a srovnání
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      AI i lidé milují strukturu. Použijte:
                    </p>
                    <ul className="space-y-2 mt-3">
                      {['Kritéria výběru (3–6 bodů)', 'Srovnání variant', 'Doporučení pro typické situace'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00d4aa]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3 text-slate-800">
                      <span className="w-8 h-8 rounded-full bg-[#14b8a6]/20 text-[#14b8a6] flex items-center justify-center text-sm font-bold">3</span>
                      Vytvořte FAQ na reálné otázky
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      FAQ má smysl, když odpovídá na skutečné bariéry:
                    </p>
                    <ul className="space-y-2 mt-4">
                      {['Kolik to stojí?', 'Jak dlouho to trvá?', 'Co dostanu?'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#14b8a6]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3 text-slate-800">
                      <Link2 className="w-5 h-5 text-[#22c55e]" />
                      Prolinkujte související obsah
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      Každý článek by měl vést dál. Minimálně:
                    </p>
                    <ul className="space-y-2 mt-3">
                      {['2–3 odkazy na související články', '1 odkaz na službu', '1 odkaz na hlavní téma'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3 text-slate-800">
                      <Wrench className="w-5 h-5 text-[#0ea5e9]" />
                      Zkontrolujte technické základy
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      Ujistěte se, že web funguje technicky: indexace, rychlost, mobilní verze. Bez toho se obsah nezobrazí.
                    </p>
                  </div>
                </div>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <CheckCircle className="w-7 h-7 text-emerald-500" />
                  Jak poznáte, že to funguje
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Dívejte se na konkrétní čísla:
                </p>
                
                <ul className="space-y-4 my-6">
                  {[
                    'Více návštěv na rozhodovacích stránkách',
                    'Delší čas na stránce u klíčových článků',
                    'Více poptávek z organického vyhledávání'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Articles */}
              <div className="animate-section my-12">
                <h3 className="text-xl font-bold mb-6 text-slate-800">Další čtení</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link
                    to="/blog/proc-lide-budou-vic-vyuzivat-ai-vyhledavani"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#00d4aa]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#00d4aa] mb-2">Trendy</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">Proč poroste AI vyhledávání: 6 důvodů</div>
                  </Link>
                  <Link
                    to="/blog/ai-seo-bez-chaosu-prvni-mesic"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#14b8a6]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#14b8a6] mb-2">Průvodce</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">AI SEO bez chaosu: 7 priorit</div>
                  </Link>
                </div>
              </div>

              {/* CTA */}
              <div className="animate-section my-12">
                <div className="glass-card rounded-2xl p-8 text-center border border-[#0ea5e9]/30">
                  <h3 className="text-2xl font-bold mb-4 text-slate-800">Chcete zjistit potenciál vašeho webu?</h3>
                  <p className="text-slate-500 mb-6">
                    Ukážeme vám, které 3–5 stránek má smysl upravit jako první.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/audit">
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                      >
                        AI Visibility Audit
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default Article1Page;
